import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Minus } from "lucide-react";

/**
 * Rendu public des blocs de contenu détaillé.
 *
 * Un `block` = { id, title, content, mode: "text" | "accordion" }
 *
 *  - mode="text" : le titre (optionnel) puis le contenu sont affichés en entier.
 *  - mode="accordion" : seul le titre est visible ; un clic sur le bouton
 *    « Lire plus / + » déplie le contenu.  Un second clic (« Lire moins / − »)
 *    replie.
 *
 * Rétro-compat : si `blocks` est vide/absent, on peut passer `legacyBody`
 * (l'ancien champ « Contenu détaillé » séparé par lignes vides) pour un rendu
 * paragraphé fluide.
 */
export default function PageBlocks({ blocks = [], legacyBody = "", className = "" }) {
  const items = Array.isArray(blocks) ? blocks : [];

  if (!items.length) {
    if (!legacyBody) return null;
    return (
      <div
        className={`text-slate-700 leading-relaxed whitespace-pre-line space-y-4 text-[15px] ${className}`}
        data-testid="page-blocks-legacy"
      >
        {legacyBody.split(/\n{2,}/).map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`} data-testid="page-blocks">
      {items.map((b, i) => (
        <BlockRenderer key={b.id || i} block={b} index={i} />
      ))}
    </div>
  );
}

function BlockRenderer({ block, index }) {
  const [open, setOpen] = useState(false);
  const isAccordion = block.mode === "accordion";
  const title = (block.title || "").trim();
  const content = block.content || "";

  if (!isAccordion) {
    return (
      <section className="pt-2" data-testid={`page-block-${index}`}>
        {title && (
          <h2 className="text-xl sm:text-2xl font-bold text-unisic-blue">{title}</h2>
        )}
        {title && <span className="divider-gold mt-3" />}
        <div className={`text-slate-700 leading-relaxed whitespace-pre-line space-y-4 text-[15px] ${title ? "mt-5" : ""}`}>
          {content.split(/\n{2,}/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </section>
    );
  }

  return (
    <div
      className="paper-card overflow-hidden border-l-4 border-l-unisic-gold"
      data-testid={`page-block-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`page-block-toggle-${index}`}
      >
        <h3 className="text-base md:text-lg font-bold text-unisic-blue leading-snug">
          {title || `Section ${index + 1}`}
        </h3>
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-unisic-blue/70 font-semibold flex-shrink-0">
          {open ? (
            <>
              <span className="hidden sm:inline">Lire moins</span>
              <Minus className="w-4 h-4" />
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Lire plus</span>
              <Plus className="w-4 h-4" />
            </>
          )}
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-5 md:px-6 pb-5 pt-1 border-t border-slate-100">
            <div className="text-slate-700 leading-relaxed whitespace-pre-line space-y-4 text-[15px]">
              {content.split(/\n{2,}/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// re-export helpers used by dashboard
export { ChevronDown, ChevronUp };
